import { Injectable } from '@angular/core';

import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { sortBy } from 'lodash-es';

export interface CorrectScoreOptions {
    layout: number;
    options: Nullable<EventOption>[];
    rest: EventOption[];
}

@Injectable({ providedIn: 'root' })
export class CorrectScoreService {
    getCorrectScoreOptions(optionGroup: EventOptionGroup, pushRestToTheMiddle: boolean = false): CorrectScoreOptions {
        if (!optionGroup) {
            throw new Error('Invalid getCorrectScoreOptions arguments. OptionGroup is required.');
        }

        if (optionGroup.options.length === 0) {
            return { layout: 3, options: [], rest: [] };
        }

        const left: EventOption[] = [];
        const middle: EventOption[] = [];
        const right: EventOption[] = [];
        const rest: EventOption[] = [];

        optionGroup.options
            .filter((option) => option && option.name)
            .forEach((option) => {
                const scores = option.name.match(/(\d+)/g);

                if (scores === null) {
                    rest.push(option);
                } else if (scores[0] > scores[1]) {
                    left.push(option);
                } else if (scores[0] < scores[1]) {
                    right.push(option);
                } else {
                    middle.push(option);
                }

                option.player1Score = scores?.[0] ? parseInt(scores[0]) : undefined;
                option.player2Score = scores?.[1] ? parseInt(scores[1]) : undefined;
            });

        return this.mapColumnOptions(left, middle, right, rest, pushRestToTheMiddle);
    }

    mapColumnOptions(
        left: EventOption[],
        middle: EventOption[],
        right: EventOption[],
        rest: EventOption[],
        pushRestToTheMiddle: boolean,
    ): CorrectScoreOptions {
        const longest = Math.max(left.length, right.length, middle.length);
        left = sortBy(left, this.optionsSortLeft);
        right = sortBy(right, this.optionsSortRight);
        middle = sortBy(middle, this.optionsSortLeft);

        if (pushRestToTheMiddle && middle.length) {
            middle = middle.concat(rest);
            rest = [];
        }

        const options: Nullable<EventOption>[] = [];
        const getOptionOrPlaceholder = (index: number, array: EventOption[]) => (index < array.length ? array[index] : null);

        for (let i = 0; i < longest; i++) {
            options.push(getOptionOrPlaceholder(i, left));

            if (middle.length > 0) {
                options.push(getOptionOrPlaceholder(i, middle));
            }

            options.push(getOptionOrPlaceholder(i, right));
        }

        return {
            layout: middle.length > 0 ? 3 : 2,
            options,
            rest,
        };
    }

    private optionsSortLeft = (option: EventOption): number => {
        const score = option.name.match(/(\d+)/g)!;

        return parseInt(score[0] + score[1]);
    };

    private optionsSortRight = (option: EventOption): number => {
        const score = option.name.match(/(\d+)/g)!;

        return parseInt(score[1] + score[0]);
    };
}
